/**
 * AppLayout Component
 * Responsive layout wrapper with Claude-style sidebar navigation
 *
 * Web (>768px): Fixed sidebar on left (~25% width), content on right
 * Mobile (<768px): Fullscreen menu OR content with back button
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  View,
  StyleSheet,
  Dimensions,
  Platform,
  TouchableOpacity,
  Text,
  Animated,
  SafeAreaView,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import SidebarMenu, { SidebarRoute } from '../navigation/SidebarMenu';
import AlertsBanner from '../common/AlertsBanner';
import DemoBanner from '../common/DemoBanner';
import { useDemoMode } from '../../contexts/DemoModeContext';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

// Breakpoint for responsive design
const TABLET_BREAKPOINT = 768;
const SIDEBAR_WIDTH_PERCENT = 0.25; // 25% on web
const SIDEBAR_MIN_WIDTH = 240;
const SIDEBAR_MAX_WIDTH = 320;

interface NavigationParams {
  scrollToGoalId?: string;
  openCsvImport?: boolean;
}

interface LayoutContextType {
  isMobile: boolean;
  isMenuVisible: boolean;
  showMenu: () => void;
  hideMenu: () => void;
  activeRoute: SidebarRoute;
  navigateTo: (route: SidebarRoute, params?: NavigationParams) => void;
  navigationParams: NavigationParams;
  clearNavigationParams: () => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within AppLayout');
  }
  return context;
};

interface AppLayoutProps {
  children: React.ReactNode;
  activeRoute: SidebarRoute;
  onNavigate: (route: SidebarRoute) => void;
  onSettingsPress?: () => void;
  onSignOut?: () => void;
  showHeader?: boolean;
  headerTitle?: string;
}

export default function AppLayout({
  children,
  activeRoute,
  onNavigate,
  onSettingsPress,
  onSignOut,
  showHeader = true,
  headerTitle,
}: AppLayoutProps) {
  const [windowWidth, setWindowWidth] = useState(Dimensions.get('window').width);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [navigationParams, setNavigationParams] = useState<NavigationParams>({});
  const slideAnim = useState(new Animated.Value(-SIDEBAR_MAX_WIDTH))[0];

  // Detect screen size changes
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setWindowWidth(window.width);
      // Auto-hide menu when switching to desktop
      if (window.width >= TABLET_BREAKPOINT) {
        setIsMenuVisible(false);
      }
    });

    return () => subscription?.remove();
  }, []);

  // Update browser tab title
  useEffect(() => {
    if (Platform.OS === 'web') {
      const title = headerTitle || getRouteTitle(activeRoute);
      document.title = `${title} - Wealfi`;
    }
  }, [activeRoute, headerTitle]);

  const isMobile = windowWidth < TABLET_BREAKPOINT;
  const sidebarWidth = isMobile
    ? windowWidth // Fullscreen on mobile
    : Math.min(
        Math.max(windowWidth * SIDEBAR_WIDTH_PERCENT, SIDEBAR_MIN_WIDTH),
        SIDEBAR_MAX_WIDTH
      );

  // Animate mobile menu
  useEffect(() => {
    Animated.timing(slideAnim, {
      toValue: isMenuVisible ? 0 : -sidebarWidth,
      duration: 250,
      useNativeDriver: true,
    }).start();
  }, [isMenuVisible, sidebarWidth]);

  const showMenu = () => setIsMenuVisible(true);
  const hideMenu = () => setIsMenuVisible(false);

  const handleNavigate = (route: SidebarRoute, params?: NavigationParams) => {
    if (isMobile) {
      hideMenu();
    }
    // Set navigation params if provided
    if (params) {
      setNavigationParams(params);
    }
    onNavigate(route);
  };

  const clearNavigationParams = () => setNavigationParams({});

  const contextValue: LayoutContextType = {
    isMobile,
    isMenuVisible,
    showMenu,
    hideMenu,
    activeRoute,
    navigateTo: handleNavigate,
    navigationParams,
    clearNavigationParams,
  };

  // Get demo mode state
  const { demoMode, profileName: demoProfileName, exitDemoMode } = useDemoMode();

  const handleExitDemo = async () => {
    try {
      await exitDemoMode();
    } catch (err) {
      console.error('[AppLayout] Failed to exit demo mode:', err);
    }
  };

  const handleImportReal = () => {
    handleNavigate('Budget', { openCsvImport: true });
  };

  // Desktop layout: fixed sidebar + scrollable content
  if (!isMobile) {
    return (
      <LayoutContext.Provider value={contextValue}>
        <View style={styles.desktopContainer}>
          {/* Fixed Sidebar - stays in place while content scrolls */}
          <View
            style={[
              styles.desktopSidebar,
              {
                width: sidebarWidth,
                // @ts-ignore - position fixed works on web
                position: Platform.OS === 'web' ? 'fixed' : 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                zIndex: 100,
              },
            ]}
          >
            <SafeAreaView style={styles.safeArea}>
              <SidebarMenu
                activeRoute={activeRoute}
                onNavigate={handleNavigate}
                onSettingsPress={onSettingsPress}
                onSignOut={onSignOut}
              />
            </SafeAreaView>
          </View>

          {/* Main Content - offset by sidebar width */}
          <View
            style={[
              styles.desktopContent,
              { marginLeft: sidebarWidth },
            ]}
          >
            <SafeAreaView style={styles.safeArea}>
              {/* Desktop Header with title and alerts */}
              <View style={styles.desktopHeaderContainer}>
                <View style={[styles.desktopHeader, demoMode && styles.desktopHeaderWithDemo]}>
                  <Text style={styles.desktopHeaderTitle}>
                    {headerTitle || getRouteTitle(activeRoute)} - Wealfi
                  </Text>
                  <AlertsBanner />
                </View>
                {/* Demo Mode Banner - integrated in header (40% when active) */}
                {demoMode && (
                  <DemoBanner
                    profileName={demoProfileName}
                    onImportReal={handleImportReal}
                    onExitDemo={handleExitDemo}
                    compact
                  />
                )}
              </View>
              {/* Content */}
              <View style={styles.desktopContentInner}>
                {children}
              </View>
            </SafeAreaView>
          </View>
        </View>
      </LayoutContext.Provider>
    );
  }

  // Mobile layout: fullscreen menu or content
  return (
    <LayoutContext.Provider value={contextValue}>
      <SafeAreaView style={styles.mobileContainer}>
        {/* Mobile Header when menu is hidden */}
        {!isMenuVisible && showHeader && (
          <View style={styles.mobileHeaderContainer}>
            <View style={[styles.mobileHeader, demoMode && styles.mobileHeaderWithDemo]}>
              <TouchableOpacity
                style={styles.menuButton}
                onPress={showMenu}
                activeOpacity={0.7}
              >
                <Icon name="menu" size={24} color={colors.text} />
              </TouchableOpacity>
              <Text style={styles.headerTitle} numberOfLines={1}>
                {headerTitle || getRouteTitle(activeRoute)} - Wealfi
              </Text>
              <AlertsBanner />
            </View>
            {/* Demo Mode Banner - integrated in header (40% when active) */}
            {demoMode && (
              <DemoBanner
                profileName={demoProfileName}
                onImportReal={handleImportReal}
                onExitDemo={handleExitDemo}
                compact
              />
            )}
          </View>
        )}

        {/* Mobile Content */}
        {!isMenuVisible && (
          <View style={styles.mobileContent}>
            {children}
          </View>
        )}

        {/* Mobile Menu (fullscreen) */}
        {isMenuVisible && (
          <View style={styles.mobileMenuContainer}>
            {/* Back button header */}
            <View style={styles.mobileMenuHeader}>
              <TouchableOpacity
                style={styles.backButton}
                onPress={hideMenu}
                activeOpacity={0.7}
              >
                <Icon name="arrow-left" size={24} color={colors.text} />
              </TouchableOpacity>
              <Text style={styles.menuHeaderTitle}>Menu</Text>
              <View style={styles.headerSpacer} />
            </View>

            {/* Menu Content */}
            <SidebarMenu
              activeRoute={activeRoute}
              onNavigate={handleNavigate}
              onSettingsPress={() => {
                hideMenu();
                onSettingsPress?.();
              }}
              onSignOut={onSignOut}
            />
          </View>
        )}
      </SafeAreaView>
    </LayoutContext.Provider>
  );
}

// Helper to get route display title
function getRouteTitle(route: SidebarRoute): string {
  const titles: Record<SidebarRoute, string> = {
    Dashboard: 'Dashboard',
    Chats: 'Chats',
    Goals: 'Objectifs',
    Budget: 'Budget',
    Settings: 'Paramètres',
  };
  return titles[route] || 'Wealfi';
}

const styles = StyleSheet.create({
  // Desktop styles
  desktopContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.background,
    height: '100%',
    // @ts-ignore - maxHeight works on web
    maxHeight: '100vh',
    // @ts-ignore - overflow works on web
    overflow: 'hidden',
  },
  desktopSidebar: {
    backgroundColor: colors.sidebar.background,
    borderRightWidth: 1,
    borderRightColor: colors.sidebar.border,
    height: '100%',
    // Ensure sidebar content doesn't scroll with page
    overflow: 'hidden',
  },
  desktopContent: {
    flex: 1,
    backgroundColor: colors.background,
    height: '100%',
    // @ts-ignore - overflow works on web
    overflow: 'hidden',
  },
  desktopHeaderContainer: {
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  desktopHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  desktopHeaderWithDemo: {
    // 60% proportion - reduce padding when demo banner is shown
    paddingVertical: 10,
  },
  desktopHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
  },
  desktopContentInner: {
    flex: 1,
    // @ts-ignore - overflow works on web
    overflow: 'hidden',
  },
  safeArea: {
    flex: 1,
    // @ts-ignore - overflow works on web
    overflow: 'hidden',
  },

  // Mobile styles
  mobileContainer: {
    flex: 1,
    backgroundColor: colors.background,
  },
  mobileHeaderContainer: {
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    // @ts-ignore - position sticky works on web
    position: Platform.OS === 'web' ? 'sticky' : 'relative',
    top: 0,
    zIndex: 100,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
  },
  mobileHeaderWithDemo: {
    // 60% proportion - reduce padding when demo banner is shown
    paddingVertical: 8,
  },
  menuButton: {
    padding: 8,
    borderRadius: 8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 17,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'center',
    marginHorizontal: 8,
  },
  headerSpacer: {
    width: 40,
  },
  mobileContent: {
    flex: 1,
  },

  // Mobile Menu styles
  mobileMenuContainer: {
    flex: 1,
    backgroundColor: colors.sidebar.background,
  },
  mobileMenuHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.sidebar.border,
  },
  backButton: {
    padding: 8,
    borderRadius: 8,
  },
  menuHeaderTitle: {
    flex: 1,
    fontSize: 17,
    fontWeight: '600',
    color: colors.sidebar.text,
    textAlign: 'center',
    marginHorizontal: 8,
  },
});
